import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Pagination, Select, SelectItem } from '@heroui/react';
import { UniversalTableProps } from '../types/account';
import Typography from './typography/Typography';
import { getButtonVariantColors } from '../theme';
import { useTranslations } from '../contexts/translation.context';
import { ArrowSortDown, ArrowSortUp } from './icons';

type Selection = 'all' | Set<React.Key>;

const UniversalTable = <T extends Record<string, any>>({ 
  data, 
  columns, 
  renderCell, 
  onRowAction,
  emptyContent,
  isSearchEmpty,
  pagination,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  currentPageSize = 5,
  sortBy,
  onSort
}: UniversalTableProps<T>) => {
  const { t } = useTranslations();

  const [currentSort, setCurrentSort] = useState(sortBy);
  const previousSortRef = useRef(sortBy);

  useEffect(() => {
    if (sortBy !== previousSortRef.current) {
      previousSortRef.current = sortBy;
      setCurrentSort(sortBy);
    }
  }, [sortBy]);

  // Вычисляем общее количество страниц
  const totalPages = useMemo(() => {
    if (!pagination) return 0;
    return Math.ceil(pagination.count / pagination.page_size);
  }, [pagination]);

  const handlePageSizeChange = useCallback((keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    const selectedValue = Array.from(keys)[0] as string;
    const newPageSize = Number(selectedValue);

    // При изменении размера страницы сбрасываем на первую страницу
    onPageChange?.(1);
    onPageSizeChange?.(newPageSize);
  }, [onPageSizeChange, onPageChange]);

  const handlePageChange = useCallback((newPage: number) => {
    onPageChange?.(newPage);
  }, [onPageChange]);

  const handleRowClick = useCallback((item: T) => {
    if (onRowAction) {
      onRowAction(item);
    }
  }, [onRowAction]);

  // Не используем useCallback - handleSort будет создаваться каждый раз с актуальным sortBy
  const handleSort = (column: typeof columns[0]) => {
    if (!column.sortable || !column.sortKey || !onSort) return;
    
    console.log('[handleSort] Current sortBy:', currentSort);
    console.log('[handleSort] Column sortKey:', column.sortKey);

    setCurrentSort((prevSort) => {
      const sortKey = column.sortKey as string;
      let nextSort: string;

      if (prevSort === sortKey) {
        nextSort = `-${sortKey}`;
        console.log('[handleSort] Switching to DESC');
      } else if (prevSort === `-${sortKey}`) {
        nextSort = sortKey;
        console.log('[handleSort] Switching to ASC');
      } else {
        nextSort = sortKey;
        console.log('[handleSort] Setting to ASC');
      }

      onSort(nextSort);
      return nextSort;
    });
  };

  const getSortIcon = useCallback((column: typeof columns[0]) => {
    if (!column.sortable || !column.sortKey) return null;

    // created_at = DESC (newest first) = стрелка вниз
    // -created_at = ASC (oldest first) = стрелка вверх
    const isDesc = currentSort === column.sortKey;
    const isAsc = currentSort === `-${column.sortKey}`;

    const activeColor = "!text-[#006FEE]";
    const inactiveColor = "text-gray-300";

    if (isDesc) {
      return (
        <span className="ml-[8px] flex items-center leading-none py-[3.88px]">
          <ArrowSortDown
            size={12}
            className={`transition-colors ${activeColor}`}
          />
        </span>
      );
    }

    if (isAsc) {
      return (
        <span className="ml-[8px] flex items-center leading-none py-[3.88px]">
          <ArrowSortUp
            size={12}
            className={`transition-colors ${activeColor}`}
          />
        </span>
      );
    }

    return (
      <span className="ml-[8px] flex flex-col items-center leading-none py-[3.88px]">
        <ArrowSortUp
          size={12}
          className={`transition-colors ${inactiveColor}`}
        />
        <ArrowSortDown
          size={12}
          className={`-mt-[3.88px] transition-colors ${inactiveColor}`}
        />
      </span>
    );
  }, [currentSort]);

  const variantColors = getButtonVariantColors('primary');
  const isPartner = window.location.pathname.startsWith('/partner');
  return (
    <div className="flex flex-col h-full relative pb-[32px]">
      <div className="relative pb-[24px] overflow-x-auto overflow-y-hidden">
        <style>{`
          .table-row:hover .table-cell:first-child {
            background-color: #f9fafb;
            border-top-left-radius: 12px;
            border-bottom-left-radius: 12px;
          }
          .table-row:hover .table-cell:last-child {
            background-color: #f9fafb;
            border-top-right-radius: 12px;
            border-bottom-right-radius: 12px;
          }
          .table-row:hover .table-cell:not(:first-child):not(:last-child) {
            background-color: #f9fafb;
          }
        `}</style>
        <Table
          aria-label={t('aria.universalTable')}
          classNames={{
            wrapper: "bg-white rounded-2xl border border-gray-200 shadow-sm",
            // thead: "bg-gray-50",
            th: "text-gray-500 font-medium text-sm h-[48px] px-[16px]",
            td: "py-[16px] px-[16px]",
            tr: "transition-colors table-row"
          }}
        >
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn 
                key={column.key} 
                className={`text-gray-500 ${
                  column.key === columns[columns.length - 1]?.key ? 'relative' : ''
                } ${column.sortable ? 'cursor-pointer hover:text-gray-700' : ''}`}
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center">
                  <span>{column.name}</span>
                  {column?.total && (
                    <span className='ml-[10px] inline-flex items-center justify-center rounded-full bg-[#f4f4f5] px-[8px] py-[2px] text-[12px] text-[#71717A]'>
                      {column.total}
                    </span>
                  )}
                  {getSortIcon(column)}
                </div>
              </TableColumn>
            )}
          </TableHeader>
          <TableBody 
            items={data}
            emptyContent={emptyContent || (
              isSearchEmpty ? (
                <div className="text-center py-[48px]">
                  <div className="flex flex-col items-center gap-[16px]">
                    <div className="flex flex-col gap-[6px]">
                      <div className="text-[20px] font-semibold leading-[28px]">
                        {t('table.noDataFound')}
                      </div>
                      <div className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]">
                        {t('table.checkDataOrTryAgain')}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    {t('table.noDataToDisplay')}
                  </div>
                </div>
              )
            )}
          >
            {(item) => (
              <TableRow 
                className='h-[38px]' 
                key={item.id || item.key || Math.random()}
                onClick={() => handleRowClick(item)}
                style={{ cursor: onRowAction ? 'pointer' : 'default' }}
              >
                {(columnKey) => {
                  const isFirstColumn = columnKey === columns[0]?.key;
                  const isLastColumn = columnKey === columns[columns.length - 1]?.key;
                  
                  return (
                    <TableCell 
                      className={`align-top table-cell ${
                        isLastColumn ? 'relative' : ''
                      }`}
                    >
                      {renderCell(item, String(columnKey))}
                    </TableCell>
                  );
                }}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.count > 0 && (
        <div className="absolute bottom-[0px] left-[0px] w-full flex items-center justify-between">
          <div className="flex items-center gap-[10px]">
            <Typography variant='body2' className="text-[#71717A]">
              {t('table.displayPerPage')}
            </Typography>
            <Select
              selectedKeys={new Set<React.Key>([currentPageSize.toString()])}
              onSelectionChange={handlePageSizeChange}
              className="w-[72px]"
              aria-label={t('aria.selectPageSize')}
              disallowEmptySelection
              classNames={{
                listboxWrapper: "!w-[72px] px-[4px] py-[2px]",
                value:'gap-[0px]'
              }}
            >
              <SelectItem className='!gap-[0px]' key="10" textValue="10">10</SelectItem>
              <SelectItem className='!gap-[0px]' key="20" textValue="20">20</SelectItem>
              <SelectItem className='!gap-[0px]' key="50" textValue="50">50</SelectItem>
            </Select>
          </div>
          
          <Pagination
            isCompact 
            total={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            showControls
            classNames={isPartner ?{
              cursor: "font-medium !bg-[#006FEE]  !rounded-[8px] !text-white",
              item: "hover:!bg-gray-100",
              wrapper: '!rounded-[8px]'
            } : {}}
            
          />
        </div>
      )}
    </div>
  );
};

export { UniversalTable };

const AccountTable = UniversalTable;

export default AccountTable; 
